import React from 'react';
import { useIntl } from 'react-intl';
import { Account } from '../hooks/useAccount';
import { Product } from '../swagger';
import { api } from '../utils/api';
import { isHealthCheckup } from '../utils/isHealthCheckup';
import { isRecreateCandidate } from '../utils/isRecreateCandidate';
import { reportError } from '../utils/reportError';
import { SubmitButton } from './forms/SubmitButton';

interface OrderProductRecreateButtonProps {
  product: Product;
}

/**
 * "Buy again" for a health checkup, on the customer's own card, for an admin
 * looking at it. The admin customers list is built from orders with live
 * insured, so a checkup that has run out never appears there — this card is
 * the one place an admin can reach it.
 *
 * The engine decides whether it applies: it also refuses one still inside
 * its paid period, since that would invoice the payer twice.
 */
export const OrderProductRecreateButton: React.FC<
  OrderProductRecreateButtonProps
> = ({ product }) => {
  const { iss, refreshAccount } = Account.useContainer();
  const { formatMessage } = useIntl();
  const [canRecreate, setCanRecreate] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const accountId = product.account?.id;
  const applies =
    iss === 'admin' &&
    isHealthCheckup(product.type) &&
    isRecreateCandidate(product.status) &&
    !!accountId;

  React.useEffect(() => {
    if (!applies || !accountId) {
      return;
    }

    let cancelled = false;

    api
      .adminControllerCanRecreateHealthCheckup({ accountId })
      .then((result) => !cancelled && setCanRecreate(!!result.canRecreate))
      .catch(() => !cancelled && setCanRecreate(false));

    return () => {
      cancelled = true;
    };
  }, [applies, accountId]);

  const recreate = React.useCallback(async () => {
    if (
      !accountId ||
      !window.confirm(formatMessage({ id: 'recreate.confirm' }))
    ) {
      return;
    }

    setLoading(true);

    try {
      await api.adminControllerRecreateHealthCheckup({ accountId });
      await refreshAccount();
    } catch (e) {
      reportError(e);
      window.alert(formatMessage({ id: 'recreate.failed' }));
    }

    setLoading(false);
  }, [accountId, formatMessage, refreshAccount]);

  if (!applies || !canRecreate) {
    return null;
  }

  return (
    <SubmitButton
      label="recreate.cta"
      loading={loading}
      onClick={recreate}
      size="small"
      color="primary"
      variant="outlined"
    />
  );
};
